import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { Select } from "antd";
import swal from "sweetalert";
import "./MedicationTable.css";

const { Option } = Select;

const CREATED_LABEL = { medicine: "Medicine", dose: "Dose", when: "When" };

// A medication row is all-or-nothing: these four fields must be present before
// the row can be committed. Instructions is deliberately optional.
export const MEDICATION_REQUIRED = [
  "medicine",
  "dose",
  "when",
  "durationValue",
];

const REQUIRED_LABELS = {
  medicine: "Medicine",
  dose: "Dose",
  when: "When",
  durationValue: "Duration",
};

export const emptyMedicationDraft = () => ({
  medicine: null,
  dose: "",
  when: "",
  durationValue: "",
  durationUnit: "Days",
  instructions: "",
});

// [] means the draft is complete and may be committed.
export const missingFields = (draft) =>
  MEDICATION_REQUIRED.filter((field) => !draft || !draft[field]);

export const formatDuration = (row) =>
  row && row.durationValue
    ? `${row.durationValue} ${row.durationUnit || "Days"}`
    : "";

const missingLabel = (missing) => {
  const names = missing.map((f) => REQUIRED_LABELS[f]);
  if (names.length === 1) return `Select a ${names[0]} to add`;
  return `Still needed: ${names.join(", ")}`;
};

const DURATION_UNITS = ["Days", "Weeks", "Months", "Years"];

// Shared renderer for the two medication sections on the Chemotherapy Sitting
// tab. Saved rows are read-only; a single draft row at the bottom is the only
// editable thing, and it is committed one row at a time through onCommit.
export default function MedicationTable({
  title,
  savedRows = [],
  draft = emptyMedicationDraft(),
  editingXrefId = null,
  medicines = [],
  doses = [],
  whens = [],
  loadingMedicines = false,
  loadingDoses = false,
  loadingWhens = false,
  onLoadMedicines,
  onLoadDoses,
  onLoadWhens,
  onCreateMedicine,
  onCreateDose,
  onCreateWhen,
  onDraftChange,
  onCommit,
  onCancelEdit,
  onEdit,
  onDelete,
  onLoadFromMedicineList,
  disabled = false,
  busy = false,
}) {
  // Search text typed into each dropdown, used by the "Create ..." affordance.
  const [newItem, setNewItem] = useState({ medicine: "", dose: "", when: "" });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [creating, setCreating] = useState(null);

  const missing = missingFields(draft);
  const canCommit = missing.length === 0 && !disabled && !busy;

  const filterOption = (input, option) => {
    if (!input) return true;
    return option.children.toLowerCase().includes(input.toLowerCase());
  };

  // Creates a new master record from whatever the user typed, then selects it
  // into the draft row.
  const handleCreate = (field, creator) => {
    const typed = (newItem[field] || "").trim();
    if (!typed || !creator) return;

    setCreating(field);
    setOpenDropdown(null);

    Promise.resolve(creator(typed))
      .then((result) => {
        if (result) {
          // A just-created medicine may not be in the master list yet, so hand
          // the record itself back rather than a name the parent has to resolve.
          onDraftChange(field, field === "medicine" ? result : result.name);
          setNewItem((prev) => ({ ...prev, [field]: "" }));
          if (field !== "medicine") {
            // saveMedicine already reports success itself; the dose and when
            // creators do not.
            swal({
              title: "Success!",
              text: `${CREATED_LABEL[field]} "${typed}" added successfully`,
              icon: "success",
              buttons: false,
              timer: 2000,
            });
          }
        }
      })
      .finally(() => setCreating(null));
  };

  const notFound = (field, creator) =>
    newItem[field] ? (
      <div
        style={{
          padding: "5px",
          cursor: "pointer",
          background: "#f0f0f0",
          borderRadius: "4px",
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCreate(field, creator);
        }}
      >
        Create "{newItem[field]}"
      </div>
    ) : (
      <div style={{ padding: "5px" }}>No data found</div>
    );

  const masterSelect = ({
    field,
    placeholder,
    options,
    loading,
    onLoad,
    creator,
    valueOf,
  }) => (
    <Select
      className="w-100"
      placeholder={placeholder}
      value={valueOf() || undefined}
      onChange={(value) => {
        onDraftChange(field, value);
        setNewItem((prev) => ({ ...prev, [field]: "" }));
        setOpenDropdown(null);
      }}
      loading={loading || creating === field}
      showSearch
      open={openDropdown === field}
      onDropdownVisibleChange={(open) => {
        setOpenDropdown(open ? field : null);
        if (open && onLoad) onLoad();
      }}
      filterOption={filterOption}
      onSearch={(value) => setNewItem((prev) => ({ ...prev, [field]: value }))}
      notFoundContent={notFound(field, creator)}
      disabled={disabled}
    >
      {options.map((item) => (
        <Option key={item.id} value={item.name}>
          {item.name}
        </Option>
      ))}
    </Select>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <strong className="mb-3">{title}</strong>
        {!disabled && onLoadFromMedicineList && (
          <Button variant="link" onClick={onLoadFromMedicineList}>
            Load from Medicine List
          </Button>
        )}
      </div>

      <div className="table-responsive">
        <table className="table table-bordered border medication-table">
          <thead>
            <tr>
              <th>#</th>
              <th>MEDICINE</th>
              <th>DOSE</th>
              <th>WHEN</th>
              <th>DURATION</th>
              <th>INSTRUCTIONS</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {savedRows.length === 0 && (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No medicines added yet.
                </td>
              </tr>
            )}

            {savedRows.map((row, index) => {
              const xrefId = row.medicine && row.medicine.medicineListXrefId;
              const isBeingEdited =
                editingXrefId !== null && editingXrefId === xrefId;

              return (
                <tr
                  key={`saved-${xrefId || index}`}
                  className={isBeingEdited ? "table-warning" : ""}
                >
                  <td>{index + 1}</td>
                  <td>{row.medicine ? row.medicine.name : ""}</td>
                  <td>{row.dose}</td>
                  <td>{row.when}</td>
                  <td>{row.duration || formatDuration(row)}</td>
                  <td>{row.instructions}</td>
                  <td className="text-nowrap">
                    {!disabled && (
                      <>
                        <span
                          title={
                            isBeingEdited
                              ? "This row is open in the draft below"
                              : "Edit this medicine"
                          }
                          onClick={() =>
                            !isBeingEdited && onEdit && onEdit(row)
                          }
                          style={{
                            cursor: isBeingEdited ? "default" : "pointer",
                            color: isBeingEdited ? "#adb5bd" : "#007bff",
                            fontSize: "16px",
                            padding: "4px 8px",
                          }}
                        >
                          ✎
                        </span>
                        <span
                          title="Remove this medicine"
                          onClick={() => onDelete && onDelete(row)}
                          style={{
                            cursor: "pointer",
                            color: "#dc3545",
                            fontSize: "18px",
                            padding: "4px 8px",
                          }}
                        >
                          ✕
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <>
          <strong className="d-block mb-2">
            {editingXrefId !== null ? "Edit Medication" : "Add Medication"}
          </strong>
          <div className="table-responsive">
            <table className="table table-bordered border medication-table">
              <tbody>
                <tr>
                  <td style={{ minWidth: "180px" }}>
                    {masterSelect({
                      field: "medicine",
                      placeholder: "Select Medicine",
                      options: medicines,
                      loading: loadingMedicines,
                      onLoad: onLoadMedicines,
                      creator: onCreateMedicine,
                      valueOf: () => (draft.medicine ? draft.medicine.name : ""),
                    })}
                  </td>
                  <td style={{ minWidth: "130px" }}>
                    {masterSelect({
                      field: "dose",
                      placeholder: "Select Dose",
                      options: doses,
                      loading: loadingDoses,
                      onLoad: onLoadDoses,
                      creator: onCreateDose,
                      valueOf: () => draft.dose,
                    })}
                  </td>
                  <td style={{ minWidth: "130px" }}>
                    {masterSelect({
                      field: "when",
                      placeholder: "Select When",
                      options: whens,
                      loading: loadingWhens,
                      onLoad: onLoadWhens,
                      creator: onCreateWhen,
                      valueOf: () => draft.when,
                    })}
                  </td>
                  <td style={{ minWidth: "170px" }}>
                    <div className="d-flex">
                      <input
                        type="text"
                        className="form-control"
                        value={draft.durationValue || ""}
                        onChange={(e) =>
                          onDraftChange(
                            "durationValue",
                            e.target.value.replace(/[^0-9]/g, "")
                          )
                        }
                        placeholder="e.g., 20"
                        style={{
                          flexGrow: 1,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                        }}
                      />
                      <select
                        className="form-control"
                        value={draft.durationUnit || "Days"}
                        onChange={(e) =>
                          onDraftChange("durationUnit", e.target.value)
                        }
                        style={{
                          width: "auto",
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                        }}
                      >
                        {DURATION_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td style={{ minWidth: "180px" }}>
                    <Form.Control
                      type="text"
                      value={draft.instructions || ""}
                      onChange={(e) =>
                        onDraftChange("instructions", e.target.value)
                      }
                      placeholder="Add instructions (optional)"
                    />
                  </td>
                  <td className="text-nowrap align-middle">
                    <span
                      title={canCommit ? "" : missingLabel(missing)}
                      className="d-inline-block"
                    >
                      <Button
                        variant="secondary"
                        onClick={onCommit}
                        disabled={!canCommit}
                        style={!canCommit ? { pointerEvents: "none" } : {}}
                      >
                        {busy ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            <span>Saving...</span>
                          </>
                        ) : editingXrefId !== null ? (
                          "Update"
                        ) : (
                          "+ Add"
                        )}
                      </Button>
                    </span>
                    {editingXrefId !== null && (
                      <Button
                        variant="link"
                        className="ml-1"
                        onClick={onCancelEdit}
                        disabled={busy}
                      >
                        Cancel
                      </Button>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          {missing.length > 0 && (
            <small className="text-muted">{missingLabel(missing)}.</small>
          )}
        </>
      )}
    </>
  );
}
